if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.__brOriginalMediaWrapper = this.__brOriginalMediaWrapper
          || this.closest('.product__media-wrapper')
          || null;
        window.BrilintonProductGallery?.ensure?.(this);

        // A same-document DOM move fires connectedCallback again. Heal only an
        // accidental move away from the Liquid-rendered media wrapper; normal
        // initial connection does not schedule a second gallery refresh.
        window.setTimeout(() => {
          if (
            this.isConnected
            && this.__brOriginalMediaWrapper?.isConnected
            && !this.__brOriginalMediaWrapper.contains(this)
          ) {
            window.BrilintonProductGallery?.refresh?.(this, {
              reason: 'gallery-reconnected',
              resetPosition: false,
            });
          }
        }, 0);
      }

      disconnectedCallback() {
        // A real section removal should destroy its controller. A transient
        // same-document move reconnects before this deferred check runs.
        window.setTimeout(() => {
          if (!this.isConnected) window.BrilintonProductGallery?.destroy?.(this);
        }, 0);
      }

      onSlideChanged(event) {
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const scrollPage = options?.scrollPage !== false;
        const controllerActiveMedia = window.BrilintonProductGallery?.setActiveMedia?.(this, mediaId, {
          prepend: Boolean(prepend),
          resetPosition: true,
          reason: options?.reason || 'media-gallery-selection',
        });

        const activeMedia = controllerActiveMedia
          || this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`)
          || this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Compatibility path for featured-product or any context where the main
        // PDP gallery controller is intentionally not loaded.
        if (!controllerActiveMedia) {
          this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          activeMedia.classList.add('is-active');

          if (prepend) {
            if (activeMedia.parentElement?.firstElementChild !== activeMedia) {
              activeMedia.parentElement?.prepend(activeMedia);
            }

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              const thumbnailItem = activeThumbnail?.closest?.('li') || activeThumbnail;
              if (thumbnailItem?.parentElement?.firstElementChild !== thumbnailItem) {
                thumbnailItem?.parentElement?.prepend(thumbnailItem);
              }
            }

            if (this.elements.viewer?.slider) this.elements.viewer.resetPages?.();
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!controllerActiveMedia && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement?.scrollTo?.({ left: activeMedia.offsetLeft });
          }

          // Preserve the approved Dawn behavior for a real customer variant
          // selection. Context synchronizers pass scrollPage:false and therefore
          // cannot take ownership of document scrolling.
          if (!scrollPage) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId || mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        const position = activeThumbnail?.dataset?.mediaPosition;
        if (position) this.announceLiveRegion(activeMedia, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo?.({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
